import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';

const {FiLayers, FiPlus, FiExternalLink} = FiIcons;

const BusinessLineWidget = () => {
  const {data, dataYear, dataAvailableYears, setDataYear} = useAuth0Data();
  const {businessLines} = data;

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...dataAvailableYears, currentYear])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDataYear(parseInt(e.target.value));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPerformanceColor = (current, goal) => {
    if (goal === 0) return 'bg-gray-500';
    const percentage = (current / goal) * 100;
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (businessLines.length === 0) {
    return (
      <Link to="/data-management/business-lines" className="block">
        <motion.div
          whileHover={{y: -2}}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiLayers} className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="ml-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">Business Lines</h3>
                  <select
                    value={dataYear}
                    onChange={handleYearChange}
                    onClick={(e) => e.preventDefault()}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium bg-white cursor-pointer"
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-indigo-600" />
            </div>
          </div>
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiPlus} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No business lines found for {dataYear}.</p>
            <p className="text-sm">Click to add business lines</p>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/data-management/business-lines" className="block">
      <motion.div
        whileHover={{y: -2}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiLayers} className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="ml-3">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">Business Lines</h3>
                <select
                  value={dataYear}
                  onChange={handleYearChange}
                  onClick={(e) => e.preventDefault()}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium bg-white cursor-pointer"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <p className="text-sm text-gray-500">{businessLines.length} business lines</p>
            </div>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-indigo-600" />
          </div>
        </div>

        <div className="space-y-4">
          {businessLines.map((line, index) => {
            const percentage = line.goal > 0 ? (line.revenue / line.goal) * 100 : 0;
            return (
              <motion.div
                key={line.id}
                initial={{opacity: 0, x: -20}}
                animate={{opacity: 1, x: 0}}
                transition={{delay: index * 0.1}}
                className="border border-gray-100 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{line.name}</h4>
                    <p className="text-sm text-gray-500">
                      {formatCurrency(line.revenue)} / {formatCurrency(line.goal)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-700">{line.margin}%</span>
                    <p className="text-xs text-gray-500">Margin</p>
                  </div>
                </div>
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>{percentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    initial={{width: 0}}
                    animate={{width: `${Math.min(percentage, 100)}%`}}
                    transition={{duration: 0.8, delay: index * 0.1}}
                    className={`h-2 rounded-full ${getPerformanceColor(line.revenue, line.goal)}`}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-xl font-bold text-indigo-600">
                {formatCurrency(businessLines.reduce((sum, line) => sum + line.revenue, 0))}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Average Margin</p>
              <p className="text-xl font-bold text-gray-900">
                {businessLines.length > 0
                  ? (businessLines.reduce((sum, line) => sum + line.margin, 0) / businessLines.length).toFixed(1)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default BusinessLineWidget;